import { defineStore, acceptHMRUpdate } from 'pinia';

export const useActionsStore = defineStore('ActionsStore', () => {
  const route = useRoute();

  const profile = useProfileStore();

  const queuedActions = ref([]);

  const _currentAction = ref<Action>();

  const _playedActions = ref<Record<string, number>>({});

  const _lastUpdatedAtActions = ref<Record<string, number>>({});

  const actionsInitEventHook = createEventHook<string>();

  const getQueuedAction = (name: string) => {
    const action = queuedActions.value.find((action) => {
      return action.name === name;
    });
    return action;
  };

  const releaseAction = (name: string) => {
    const newQueue = queuedActions.value.reduce((newQ, action) => {
      if (action.name !== name) {
        newQ.push(action);
      } else {
        action.kill();
      }
      return newQ;
    }, []);
    queuedActions.value = [...newQueue];
    if (_currentAction.value && _currentAction.value?.name === name) {
      _skipCurrentAction();
    }
  };

  const _getPlayedActionCount = (name: string) => {
    return _playedActions.value[name] || 0;
  };

  const _setPlayed = (name: string) => {
    _playedActions.value[name] = _getPlayedActionCount(name) + 1;
  };

  const completeAction = (name: string, success: boolean) => {
    if (_currentAction?.value?.name !== name) {
      throw new Error(
        `Action recieve completeAction from action ${name} but the _currentAction is ${_currentAction?.value?.name}`
      );
    }
    if (success) _setPlayed(name);
    _currentAction.value = null;
    _next();
  };

  const isCurrentAction = (name: string): boolean => {
    return _currentAction?.value?.name === name;
  };

  const _skipCurrentAction = () => {
    _currentAction.value = null;
    _next();
  };

  const _setLastUpdatedAt = (name: string) => {
    _lastUpdatedAtActions.value[name] = new Date().getTime();
  };

  const actionHasBeenPlayed = (name) => {
    if (_currentAction.value && _currentAction.value.name === name) return true;

    return _getPlayedActionCount(name) > 0;
  };

  const _isActionMinDelayReached = (action: Action) => {
    if (action.minDelayCall) {
      const lastUpdated = _lastUpdatedAtActions.value[action.name] || 0;
      const now = new Date().getTime();
      return now - lastUpdated >= action.minDelayCall;
    }
    return true;
  };

  const _play = async () => {
    if (_currentAction.value) return;
    _currentAction.value = queuedActions.value.shift();

    if (_currentAction.value) {
      if (_isActionMinDelayReached(_currentAction.value)) {
        try {
          _setLastUpdatedAt(_currentAction.value.name);
          await _currentAction.value.play();
        } catch (e) {
          console.error(e);
          _skipCurrentAction();
        }
      } else {
        _skipCurrentAction();
      }
    }
  };

  const _getAllActions = () => {
    useActionSelectUser();
    useActionRefreshData();
    useActionRefreshProfile();
    if (profile.featureFlags.feed) useActionRefreshFeed();
    useActionRefreshAssessments();
    useActionRefreshGaming();
    // useActionRefreshFavorites();
    useActionCheckNeedRefreshProfiling();
    useActionSetDisplayNameAndAvatar();
    useActionPlayTutorial();
    useActionProfileRequest();
    useActionDialogNewSubscriber();
    useActionDialogFamilyNewMember();
    useActionDialogFamilyNewChildShareData();
    if (profile.featureFlags.friend) useActionShowDialogFriendsChallenge();
  };

  const enqueue = (action: Action) => {
    if (action.onlyOnce && actionHasBeenPlayed(action.name)) return;
    if (queuedActions.value.find((ac) => ac.name === action.name)) return;
    if (_currentAction.value && _currentAction.value.name === action.name)
      return;
    queuedActions.value.push(action);
  };

  const _next = () => {
    _play();
  };

  const _loadActions = () => {
    const auth = route.meta.auth != null ? route.meta.auth : true;
    const locked = route.meta.locked != null ? route.meta.locked : false;

    if (!auth || locked) return;
    _getAllActions();
  };

  const clear = () => {
    queuedActions.value = [];
  };

  const start = () => {
    // start is called every hooks call page:finish (cf plugins/ini.client.ts)
    _loadActions();
    actionsInitEventHook.trigger('Init');
    _play();
  };

  return {
    _playedActions,
    // setPlayed,
    actionHasBeenPlayed,
    enqueue,
    _currentAction,
    // _next,
    start,
    clear,
    completeAction,
    queuedActions,
    getQueuedAction,
    onActionsInit: actionsInitEventHook.on,
    releaseAction,
    isCurrentAction,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useActionsStore, import.meta.hot));
}
